import type { AdminAccount } from '../../types'
import { normalizeSecurityCode } from '@emporion/config/security-validators'
import {
  requestAuthedJson,
  resolveTokenFromAuthHeaders,
  type LegacyAuthHeaders,
} from '../../lib/api/http'

type Translator = (key: string, params?: Record<string, string | number>) => string

export type TwoFactorSetupPayload = {
  method: 'totp'
  manualEntryKey: string
  otpauthUri: string
  pendingCreatedAt: string
}

export type TwoFactorVerifyPayload = {
  enabled: boolean
  method: 'totp'
  recoveryCodes?: string[]
  recoveryCodesRemaining?: number
  recoveryCodesAcknowledgedAt?: string | null
  hasUnacknowledgedRecoveryCodes?: boolean
}

export type TwoFactorFactorVerifyPayload = {
  valid: boolean
  factorType: 'otp' | 'recovery' | null
}

export const requestTwoFactorSetup = async ({
  authHeaders,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  t: Translator
}): Promise<{ account?: AdminAccount; setup?: TwoFactorSetupPayload }> => {
  const token = resolveTokenFromAuthHeaders(authHeaders)
  const payload = await requestAuthedJson({
    token,
    path: '/admin/profile/2fa/setup',
    init: {
      method: 'POST',
      headers: authHeaders(),
    },
    fallbackError: t('profile.security.twoFactor.error.setup'),
  })

  const data = payload as { account?: AdminAccount; setup?: TwoFactorSetupPayload }
  return {
    account: data?.account,
    setup: data?.setup,
  }
}

export const requestTwoFactorVerify = async ({
  authHeaders,
  code,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  code: string
  t: Translator
}): Promise<{ account?: AdminAccount; twoFactor?: TwoFactorVerifyPayload }> => {
  const normalizedCode = normalizeSecurityCode(code)
  const token = resolveTokenFromAuthHeaders(authHeaders)
  const payload = await requestAuthedJson({
    token,
    path: '/admin/profile/2fa/verify',
    init: {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ code: normalizedCode }),
    },
    fallbackError: t('profile.security.twoFactor.error.verify'),
  })

  const data = payload as { account?: AdminAccount; twoFactor?: TwoFactorVerifyPayload }
  return {
    account: data?.account,
    twoFactor: data?.twoFactor,
  }
}

export const requestTwoFactorFactorVerify = async ({
  authHeaders,
  factorCode,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  factorCode: string
  t: Translator
}): Promise<TwoFactorFactorVerifyPayload> => {
  const normalizedFactorCode = normalizeSecurityCode(factorCode)
  const token = resolveTokenFromAuthHeaders(authHeaders)
  const payload = await requestAuthedJson({
    token,
    path: '/admin/profile/2fa/factor/verify',
    init: {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ code: normalizedFactorCode, recoveryCode: normalizedFactorCode }),
    },
    fallbackError: t('profile.security.twoFactor.error.verify'),
  })

  const data = payload as { valid?: boolean; factorType?: 'otp' | 'recovery' | null }
  return {
    valid: data?.valid === true,
    factorType: data?.factorType ?? null,
  }
}

export const requestTwoFactorDisable = async ({
  authHeaders,
  input,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  input: { code?: string; recoveryCode?: string }
  t: Translator
}): Promise<{ account?: AdminAccount }> => {
  const token = resolveTokenFromAuthHeaders(authHeaders)
  const payload = await requestAuthedJson({
    token,
    path: '/admin/profile/2fa/disable',
    init: {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    },
    fallbackError: t('profile.security.twoFactor.error.disable'),
  })

  const data = payload as { account?: AdminAccount }
  return {
    account: data?.account,
  }
}

export const requestTwoFactorRecoveryRegenerate = async ({
  authHeaders,
  code,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  code: string
  t: Translator
}): Promise<{ account?: AdminAccount; twoFactor?: TwoFactorVerifyPayload }> => {
  const normalizedCode = normalizeSecurityCode(code)
  const token = resolveTokenFromAuthHeaders(authHeaders)
  const payload = await requestAuthedJson({
    token,
    path: '/admin/profile/2fa/recovery/regenerate',
    init: {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ code: normalizedCode }),
    },
    fallbackError: t('profile.security.twoFactor.error.recoveryRegenerate'),
  })

  const data = payload as { account?: AdminAccount; twoFactor?: TwoFactorVerifyPayload }
  return {
    account: data?.account,
    twoFactor: data?.twoFactor,
  }
}

export const requestTwoFactorRecoveryAcknowledge = async ({
  authHeaders,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  t: Translator
}): Promise<{ account?: AdminAccount; twoFactor?: TwoFactorVerifyPayload }> => {
  const token = resolveTokenFromAuthHeaders(authHeaders)
  const payload = await requestAuthedJson({
    token,
    path: '/admin/profile/2fa/recovery/acknowledge',
    init: {
      method: 'POST',
      headers: authHeaders(),
    },
    fallbackError: t('profile.security.twoFactor.error.recoveryAcknowledge'),
  })

  const data = payload as { account?: AdminAccount; twoFactor?: TwoFactorVerifyPayload }
  return {
    account: data?.account,
    twoFactor: data?.twoFactor,
  }
}
